import { type FormEvent, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { Dialog } from './Dialog.js';

const { t } = i18n;

export function RecordPaymentDialog({
  customerId,
  apiBase,
  onClose,
  onRecorded,
}: {
  customerId?: string;
  /** Owner API base, e.g. `/partners/<id>`. Defaults to the customer path. */
  apiBase?: string;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const base = apiBase ?? `/customers/${customerId}`;
  const today = new Date().toISOString().slice(0, 10);
  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState(today);
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const cents = Math.round(Number(amount.replace(',', '.')) * 100);
    if (!Number.isFinite(cents) || cents <= 0) {
      setError(t('payment.create.invalid_amount'));
      return;
    }
    setBusy(true);
    try {
      await api(`${base}/payments`, {
        method: 'POST',
        body: {
          amountCents: cents,
          currency: 'EUR',
          paidAt,
          periodFrom: periodFrom || undefined,
          periodTo: periodTo || undefined,
          reference: reference || undefined,
        },
      });
      onRecorded();
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('customer.action.record_payment')} onClose={onClose}>
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-3 text-sm">
        <label className="block space-y-1">
          <span className="font-medium">{t('payment.col.amount')} (EUR)</span>
          <input
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            className="w-full rounded border border-border bg-background px-2 py-1.5 tabular-nums"
            placeholder="0.00"
          />
        </label>
        <label className="block space-y-1">
          <span className="font-medium">{t('payment.col.paid_at')}</span>
          <input
            type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
            required
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block space-y-1">
            <span className="font-medium">{t('payment.col.period_from')}</span>
            <input
              type="date"
              value={periodFrom}
              onChange={(e) => setPeriodFrom(e.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            />
          </label>
          <label className="block space-y-1">
            <span className="font-medium">{t('payment.col.period_to')}</span>
            <input
              type="date"
              value={periodTo}
              onChange={(e) => setPeriodTo(e.target.value)}
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            />
          </label>
        </div>
        <label className="block space-y-1">
          <span className="font-medium">{t('payment.col.reference')}</span>
          <input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>

        {error ? <div className="text-red-600">{error}</div> : null}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !amount}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
